import React, { useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    MenuItem,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import { LANGUAGES } from '../constants/languages';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.herbieterms.com';

function NewsletterCompareInner() {
    const navigate = useNavigate();

    const [textA, setTextA] = useState('');
    const [textB, setTextB] = useState('');
    const [language, setLanguage] = useState('en');
    const [productName, setProductName] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [result, setResult] = useState(null);

    const handleCompare = async () => {
        setErrorMessage('');
        setResult(null);

        if (!textA.trim() || !textB.trim()) {
            setErrorMessage('Please paste both Version A and Version B.');
            return;
        }

        setLoading(true);
        try {
            const resp = await axios.post(
                `${API_BASE}/newsletter/compare`,
                {
                    textA,
                    textB,
                    language,
                    productName: productName.trim() || null,
                },
                {
                    withCredentials: true,
                    headers: { 'x-api-key': process.env.REACT_APP_API_KEY },
                }
            );
            setResult(resp.data);
        } catch (e) {
            console.error(e);
            setErrorMessage(
                e?.response?.data?.message || 'Failed to compare versions. Check server logs.'
            );
        } finally {
            setLoading(false);
        }
    };

    const renderScoreTable = (scores) => {
        if (!scores) return <Typography>No scores available.</Typography>;
        return (
            <Table size="small" sx={{ mt: 1 }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Dimension</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Object.entries(scores).map(([key, value]) => (
                        <TableRow key={key}>
                            <TableCell>{key}</TableCell>
                            <TableCell>{value}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1.5,
                    mb: 2,
                }}
            >
                <Typography variant="h4" fontWeight={700}>
                    Compare newsletter versions
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" onClick={() => navigate('/')}>
                        Home
                    </Button>
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/newsletter-review')}
                    >
                        Newsletter review
                    </Button>
                </Box>
            </Box>

            <Card variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2, borderRadius: 3 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '180px minmax(0, 1fr)' },
                        gap: 1.5,
                        mb: 1.5,
                    }}
                >
                    <TextField
                        label="Language"
                        select
                        size="small"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value)}
                    >
                        {LANGUAGES.map(({ code, name }) => (
                            <MenuItem key={code} value={code}>
                                {name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Product context (optional)"
                        size="small"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        fullWidth
                    />
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                        gap: 1.5,
                    }}
                >
                    <TextField
                        label="Version A"
                        value={textA}
                        onChange={(e) => setTextA(e.target.value)}
                        fullWidth
                        multiline
                        minRows={8}
                    />
                    <TextField
                        label="Version B"
                        value={textB}
                        onChange={(e) => setTextB(e.target.value)}
                        fullWidth
                        multiline
                        minRows={8}
                    />
                </Box>

                <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    <Button variant="contained" onClick={handleCompare} disabled={loading}>
                        {loading ? 'Working…' : 'Compare'}
                    </Button>
                    <Button
                        variant="text"
                        onClick={() => {
                            setTextA('');
                            setTextB('');
                            setResult(null);
                            setErrorMessage('');
                            setProductName('');
                            setLanguage('en');
                        }}
                        disabled={loading}
                    >
                        Clear
                    </Button>
                </Box>

                {errorMessage && (
                    <Typography sx={{ mt: 1.5 }} color="error">
                        {errorMessage}
                    </Typography>
                )}
            </Card>

            {result && (
                <Card variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3 }}>
                    <Typography variant="h6" fontWeight={700}>
                        Comparison result
                    </Typography>
                    <Typography sx={{ mt: 1 }}>
                        <strong>Winner:</strong> {result.winner}
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                            gap: 2,
                            mt: 1.5,
                        }}
                    >
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                                Version A scores
                            </Typography>
                            {renderScoreTable(result.scoresA)}
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                                Version B scores
                            </Typography>
                            {renderScoreTable(result.scoresB)}
                        </Box>
                    </Box>

                    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
                        Reasons
                    </Typography>
                    {result.reasons?.length ? (
                        <Box component="ul" sx={{ mt: 0.5, mb: 0 }}>
                            {result.reasons.map((reason, index) => (
                                <li key={index}>
                                    <Typography>{reason}</Typography>
                                </li>
                            ))}
                        </Box>
                    ) : (
                        <Typography color="text.secondary">No reasons provided.</Typography>
                    )}

                    <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2 }}>
                        Suggested merged version
                    </Typography>
                    <Box sx={{ p: 1.5, mt: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>{result.suggestedMerge}</Typography>
                    </Box>
                </Card>
            )}
        </Box>
    );
}

export default function NewsletterCompare() {
    return (
        <Authenticator>
            {({ signOut }) => (
                <Box>
                    <NewsletterCompareInner />
                    <Box sx={{ px: 3, pb: 2 }}>
                        <Button size="small" onClick={signOut}>
                            Sign out
                        </Button>
                    </Box>
                </Box>
            )}
        </Authenticator>
    );
}
